import { FCX, ID } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { FilterInputNameEnum } from "@vms/SelectFilters/getFiltersChanges";
import { FilterWord, TagCategoryItem, TagItem } from "./SelectFiltersTypes";

const MAX_RESULTS = 16;

type TooltipOption = {
    id: ID;
    name: string;
};

export const TooltipOptionsList: FCX<{
    data: TooltipOption[];
}> = ({
    data,
    className,
}) => (
    <div className={cn("TooltipOptionsList", className)}>
        {data.slice(0, MAX_RESULTS).map((option) => (
            <div
                key={`TooltipOption${option.id}`}
                className="TooltipOptionsList__option"
            >
                {option.name}
            </div>
        ))}
        {data.length > MAX_RESULTS && (
            <div
                key="TooltipOptionMore"
                className="TooltipOptionsList__option"
            >
                ...and {data.length - MAX_RESULTS} more
            </div>
        )}
    </div>
);

const getRangeFilter = (min?: string[], max?: string[]): string | null => {
    if (min && max) return `${min[0]} - ${max[0]}`;
    if (min) return `More than ${min[0]}`;
    if (max) return `Less than ${max[0]}`;
    return null;
};

const getCateringFilter = (catering?: string): string | null => {
    if (catering === 'catering only') return 'Catering menu items';
    if (catering === 'include catering') return 'Regular + Catering menu items';
    return null;
};

const getAdditionalAttributesFilter = (changes: Record<string, string[]>) => {
    const attributes = [
        changes[FilterInputNameEnum.HealthyTerms]?.[0],
        changes[FilterInputNameEnum.SmallPlates]?.[0],
        changes[FilterInputNameEnum.KidMenus]?.[0],
        changes[FilterInputNameEnum.GlutenFree]?.[0],
        changes[FilterInputNameEnum.PlantBased]?.[0],
    ].filter(Boolean);

    if (attributes.length === 0) return null;

    const title = attributes.length === 1 ? attributes[0] : `Additional filters (${attributes.length})`;

    return {
        id: 'additional attributes',
        title,
        tooltip: attributes.length > 1 ? (
            <TooltipOptionsList
                data={attributes.map((o) => ({ id: o, name: o }))}
            />
        ) : null,
    };
};

export const getVenueFilters = (changes: Record<string, string[]>) => {
    const value = getRangeFilter(
        changes[FilterInputNameEnum.MinUnits],
        changes[FilterInputNameEnum.MaxUnits]
    );
    if (!value) return [];
    return [{ id: 'units range', title: `Units Count: ${value}` }];
};

export const getAttributesFilters = (changes: Record<string, string[]>) => {
    const cateringValue = getCateringFilter(changes[FilterInputNameEnum.Catering]?.[0]);
    const additionalAttributesFilter = getAdditionalAttributesFilter(changes);

    return [
        ...(cateringValue ? [{ id: 'catering', title: cateringValue }] : []),
        ...(additionalAttributesFilter ? [additionalAttributesFilter] : []),
    ];
};

const groupTagsByCategory = (tags: TagItem[]) =>
    tags.reduce((acc, tag) => {
        const group = acc.get(tag.categoryId) ?? [];
        group.push(tag);
        acc.set(tag.categoryId, group);
        return acc;
    }, new Map<number, TagItem[]>());

const getTagCategoryFilter = (
    categoryId: number,
    tags: TagItem[],
    tagCategories: TagCategoryItem[]
) => {
    const categoryName = (tagCategories.find((c) => c.id === categoryId) as TagCategoryItem).title;
    const isSingle = tags.length === 1;

    return {
        id: `tag category ${categoryName}`,
        title: isSingle ? `${categoryName}: ${tags[0].title}` : `${categoryName} (${tags.length})`,
        tooltip: isSingle ? null : (
            <TooltipOptionsList data={tags.map((t) => ({ id: t.id, name: t.title }))} />
        ),
    };
};

export const getTagsFilters = (
    changes: Record<string, string[]>,
    allTags: TagItem[],
    tagCategories: TagCategoryItem[]
) => {
    const tagIds = changes[FilterInputNameEnum.Tags] ?? [];
    if (tagIds.length === 0) return [];

    const selectedTags = allTags.filter((tag) => tagIds.includes(String(tag.id)));
    const tagsByCategory = groupTagsByCategory(selectedTags);

    return Array.from(tagsByCategory.entries())
        .map(([categoryId, tags]) => getTagCategoryFilter(categoryId, tags, tagCategories))
};

export function getOtherFilters (
    changes: Record<string, string[]>,
) {
    const minPrice = changes[FilterInputNameEnum.MinPrice];
    const maxPrice = changes[FilterInputNameEnum.MaxPrice];

    const priceFilterValue = getRangeFilter(minPrice, maxPrice);

    if (priceFilterValue) {
        return [{
            id: 'price range',
            title: `Price: ${priceFilterValue}`,
        }];
    }

    return [];
}
